// The PWA running inside the iOS or Android app (docs/superpowers/specs/2026-09-24-native-app-design.md, D10). The shell loads the
// chat host itself, and Capacitor's bridge is there before any script runs: detected from it, so the browser build never loads the
// Capacitor plugins (they are imported only inside the app).

export type NativePlatform = 'ios' | 'android'

interface CapacitorGlobal {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
}

function bridge(): CapacitorGlobal | undefined {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor
}

/** Inside the BOSS Chat app, rather than a browser. */
export function isNative(): boolean {
  return bridge()?.isNativePlatform?.() === true
}

export function nativePlatform(): NativePlatform | null {
  if (!isNative()) return null
  const platform = bridge()?.getPlatform?.()
  return platform === 'ios' || platform === 'android' ? platform : null
}

/** base64url, no padding (RFC 7636). */
export function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** A PKCE verifier and its S256 challenge: the code the server hands back works only with the verifier, which never leaves the app. */
export async function pkce(random: (n: number) => Uint8Array = (n) => crypto.getRandomValues(new Uint8Array(n))): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64Url(random(32))
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)))
  return { verifier, challenge: base64Url(digest) }
}
